//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ActivityEnum, AlertLevelEnum, ChainError, ObjectTypeEnum, yupEthAddrRequired } from './Enums'
import { ETH_ADDR_ZERO, EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

import { BytesLike, Contract } from 'ethers'

import { toUTCDateString } from './conversions'

import {
  PoolAbi,
  PoolDynamicAbi,
  PoolFlexAbi,
  LYTPoolAbi,
  LYTPoolControllerAbi,
  PoolControllerDynamicAbi,
  PoolControllerFlexAbi,
  PoolAccessControlAbi,
} from './Abis'
import { logOT } from './loggingShared'

function findFirstSubstring(s: string): string | null {
  const regex = /'([^']*)'/
  const match = s.match(regex)
  return match ? match[1] : null
}
const errorMap: Record<string, string> = {
  'NotLender()': 'Your Lender Address is not registered as a valid lender for this Pool',
}
const CUSTOM_ERROR_PREFIX = 'Error: VM Exception while processing transaction: reverted with custom error'
const CUSTOM_ERROR_PREFIX2 = 'Error: VM Exception while processing transaction: reverted with reason string'
export const AlertCTOSchema = yup.object({
  title: yup
    .string()
    .required()
    .label('title')
    .meta(toFieldMetaData({ propName: 'title' })),
  functionName: yup
    .string()
    .optional()
    .label('functionName')
    .meta(toFieldMetaData({ propName: 'functionName' })),
  description: yup
    .string()
    .required()
    .label('description')
    .meta(toFieldMetaData({ propName: 'description' })),
  sourceObjectType: yup
    .mixed<ObjectTypeEnum>()
    .oneOf(Object.values(ObjectTypeEnum))
    .optional()
    .label('sourceObjectType')
    .meta(toFieldMetaData({ propName: 'sourceObjectType', choices: Object.keys(ObjectTypeEnum) })),
  sourceObjectAddr: yupEthAddrRequired.label('sourceObjectAddr').meta(
    toFieldMetaData({
      propName: 'sourceObjectAddr',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  alertLevel: yup
    .mixed<AlertLevelEnum>()
    .oneOf(Object.values(AlertLevelEnum))
    .label('alertLevel')
    .meta(toFieldMetaData({ propName: 'alertLevel', choices: Object.keys(AlertLevelEnum) })),

  activity: yup
    .mixed<ActivityEnum>()
    .oneOf(Object.values(ActivityEnum))
    .label('activity')
    .meta(toFieldMetaData({ propName: 'activity', choices: Object.keys(ActivityEnum) })),
  createdAt: yup
    .number()
    .required()
    .label('Created')
    .meta(toFieldMetaData({ propName: 'created', stringType: StringTypeEnum.ETH_DATE })),
  dueDateTime: yup
    .number()
    .required()
    .label('Due Date Time')
    .meta(toFieldMetaData({ propName: 'dueDateTime', stringType: StringTypeEnum.ETH_DATE })),
})

export interface UserError {
  message: string
  action: string
  timestamp: number
  error?: Error
}
export interface AlertCTO extends InferType<typeof AlertCTOSchema> {
  title: string
  functionName?: string
  sourceObjectAddr: EthAddr
  sourceObjectType?: ObjectTypeEnum
  description: string
  alertLevel: AlertLevelEnum
  activity?: ActivityEnum
  txHash?: string
  errorNumber?: number
}
export interface EthersError {
  error: EthersError
  reason?: string
  code?: string
  method?: string
  transaction?: {
    to: EthAddr
    data: string
    accessList: unknown
    hash: string
  }

  message: string
}

export function instanceOfAlertCTO(object: object): object is AlertCTO {
  return 'title' in object && 'alertLevel' in object && 'activity' in object
}
export function instanceOfEthersError(object: object): object is EthersError {
  return 'reason' in object
}

export function instanceOfUserError(object: object): object is UserError {
  return 'message' in object && 'action' in object && 'timestamp' in object
}

export interface Result extends ReadonlyArray<unknown> {
  readonly [key: string]: unknown
}
interface ErrorDescription {
  args: Result
  name: string
}

const decoderErrorToDescription = (decodedError: ErrorDescription): string => {
  if (decodedError.name == 'AccuralCalledTooEarly') {
    const arg0 = Number(decodedError.args[0] as bigint)
    const arg1 = Number(decodedError.args[1] as bigint)
    const midnightDate = toUTCDateString(arg0)
    const lastAccruedDate = toUTCDateString(arg1)
    return `AccrualCalledTooEarly date ${midnightDate} lastAccruedDate ${lastAccruedDate} `
  }

  return `${decodedError.name}, ${JSON.stringify(decodedError.args)}`
}

export const alertCTOFromError = (error: EthersError | UserError | Error, activity: ActivityEnum): AlertCTO => {
  const ABIsToCheck = [
    LYTPoolAbi,
    PoolDynamicAbi,
    PoolFlexAbi,
    LYTPoolControllerAbi,
    PoolControllerDynamicAbi,
    PoolControllerFlexAbi,
    PoolAbi,
    PoolAccessControlAbi,
  ]
  for (const abi of ABIsToCheck) {
    try {
      const eAny = error as any

      if (eAny?.data) {
        const contract = new Contract(ETH_ADDR_ZERO, abi)

        const decodedError = contract.interface.parseError(eAny.data as unknown as BytesLike) as ErrorDescription
        logOT.info(`#alertCTOFromError  decodedError`, decodedError)
        return {
          title: decodedError.name,
          description: decoderErrorToDescription(decodedError),
          alertLevel: AlertLevelEnum.ERROR,
          sourceObjectAddr: ETH_ADDR_ZERO,
          activity,
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
        }
      }
    } catch (e2) {
      logOT.debug('Error in parsing PoolAbi eIn.error?.data?.originalError.data ', e2)
    }
  }
  const eIn = error as ChainError
  if (eIn?.data?.originalError) {
    for (const abi of ABIsToCheck) {
      try {
        const poolContract = new Contract(ETH_ADDR_ZERO, abi)
        //      const data = eIn?.data?.originalError.data as unknown as BytesLike

        const decodedError = poolContract.interface.parseError(
          eIn?.data?.originalError.data as unknown as BytesLike
        ) as ErrorDescription

        logOT.info('alertCTOFromError 4')
        return {
          title: decodedError.name,
          description: decoderErrorToDescription(decodedError),
          alertLevel: AlertLevelEnum.ERROR,
          sourceObjectAddr: ETH_ADDR_ZERO,
          activity,
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
        }
      } catch (e2) {
        logOT.debug('Error in parsing   PoolAbi  eIn?.data?.originalError.data', e2)
      }
    }
  }

  if (instanceOfEthersError(error)) {
    logOT.info('alertCTOFromError instanceOfEthersError 1')
    logOT.info('\n\ninstanceOfEthersError message', error.message)
    logOT.info('\n\ninstanceOfEthersError error', error.error)
    logOT.info('\n\ninstanceOfEthersError error.reason', error.reason)
    logOT.info('\n\ninstanceOfEthersError error.error.reason', error.error?.reason)

    const txString = error.transaction ? JSON.stringify(error.transaction, null, 2) : ''
    logOT.info('txString', txString)
    let title = error?.reason ?? error?.message ?? 'Unknown Error'

    logOT.info('instanceOfEthersError Error', error)

    if (error.error?.reason && error.reason) {
      const t1 = error.error?.reason
      const t2 = error.reason

      logOT.info('instanceOfEthersError t1 t2', t1, t2)

      if (t1.startsWith(CUSTOM_ERROR_PREFIX) || t1.startsWith(CUSTOM_ERROR_PREFIX2)) {
        title = t1
      } else if (t2.startsWith(CUSTOM_ERROR_PREFIX) || t2.startsWith(CUSTOM_ERROR_PREFIX2)) {
        title = t2
      } else {
        title = t2
      }
    } else if (error.reason) {
      title = error.reason
    } else if (error.error?.reason) {
      title = error.error?.reason
    }
    logOT.info('title', title)
    let description = error.message

    if (title.startsWith(CUSTOM_ERROR_PREFIX) || title.startsWith(CUSTOM_ERROR_PREFIX2)) {
      const errorString = findFirstSubstring(title)
      if (errorString) {
        title = errorString ?? title
        description = errorMap[errorString] ?? errorString
      }
    }

    logOT.info('title', title)
    return {
      title,
      description,
      alertLevel: AlertLevelEnum.ERROR,
      sourceObjectAddr: ETH_ADDR_ZERO,
      activity,
      createdAt: Date.now() / 1000,
      dueDateTime: Date.now() / 1000,
      txHash: error.transaction?.hash as string,
    }
  } else if (instanceOfUserError(error)) {
    logOT.info('instanceOfUserError', error)
    return {
      title: error.message,
      description: error.error?.message ?? 'Unknown Error',
      alertLevel: AlertLevelEnum.ERROR,
      sourceObjectAddr: ETH_ADDR_ZERO,
      activity,
      createdAt: Date.now() / 1000,
      dueDateTime: Date.now() / 1000,
    }
  } else {
    logOT.info('instanceOfError', error)
    return {
      title: error.message,
      description: error.message,
      alertLevel: AlertLevelEnum.ERROR,
      sourceObjectAddr: ETH_ADDR_ZERO,
      activity,
      createdAt: Date.now() / 1000,
      dueDateTime: Date.now() / 1000,
    }
  }
}
